import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { GriddSnapshot } from "@gridd/embed/react";
import type { RequestSnapshots } from "@/components/deck/snapshotRegistry";

// Deferred grid snapshots that feed the slide-nav thumbnails. A live editor grid is expensive
// to re-render, so instead of mounting a second embed we reuse the snapshot machinery: after
// the user edits a grid — and once on mount — we pull a static PNG off the already-painted
// editor embed and cache it here, keyed by griddDocId. These images are then composited into
// the full-slide thumbnails (see slideThumbnails.tsx) in place of the cross-origin iframe.
// Capture is debounced and run at idle so it never competes with typing.

// The capture DPR. 0.5 of the grid's logical box keeps it crisp once composited into a
// full-slide thumbnail and re-rasterized; still a small PNG.
const THUMB_SCALE = 0.5;
// Coalesce bursts of edits (onDocumentChange is itself ~400ms-debounced upstream) into one
// capture, and let the grid settle first.
const THUMB_DEBOUNCE_MS = 600;
// On first mount the embed's iframe may not have loaded yet, so `requestSnapshot` rejects and
// the batch comes back empty. Back off and retry a few times to catch the initial render.
const THUMB_RETRY_MS = 1500;
const THUMB_MAX_ATTEMPTS = 6;

// Run `fn` when the browser is idle, falling back to a microtask where unsupported.
function onIdle(fn: () => void) {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(() => fn(), { timeout: 1000 });
  } else {
    fn();
  }
}

// Owns the thumbnail cache and the deferred-capture scheduler. Lives in the editor (next to
// the snapshot registry, whose `requestSnapshots` it borrows), so it only ever pulls from
// live editor embeds — the print/present overlays never schedule captures.
export function useThumbnails(requestSnapshots: RequestSnapshots) {
  const [thumbnails, setThumbnails] = useState<Record<string, GriddSnapshot>>(
    {},
  );
  // One pending timer per docId — a fresh schedule for a doc replaces its predecessor, so
  // rapid edits collapse to a single trailing capture.
  const timers = useRef(new Map<string, number>());
  // Latest `requestSnapshots` without re-creating the scheduler each render.
  const requestRef = useRef(requestSnapshots);
  requestRef.current = requestSnapshots;

  // Reassigned every render (so it always closes over fresh state setters) but referenced only
  // through the ref, so `scheduleThumbnail` below stays stable. `attempt` drives the
  // load-not-ready retry back-off.
  const scheduleRef = useRef<
    (docId: string, delay: number, attempt: number) => void
  >(() => {});
  scheduleRef.current = (docId, delay, attempt) => {
    const prev = timers.current.get(docId);
    if (prev !== undefined) window.clearTimeout(prev);
    const timer = window.setTimeout(() => {
      timers.current.delete(docId);
      onIdle(async () => {
        const map = await requestRef.current([docId], { scale: THUMB_SCALE });
        const snap = map[docId];
        if (snap) {
          setThumbnails((prevThumbs) => ({ ...prevThumbs, [docId]: snap }));
        } else if (attempt + 1 < THUMB_MAX_ATTEMPTS) {
          // No snapshot yet — the embed is likely still loading. Retry with back-off.
          scheduleRef.current(docId, THUMB_RETRY_MS, attempt + 1);
        }
      });
    }, delay);
    timers.current.set(docId, timer);
  };

  // Public: (re)capture this grid's thumbnail, deferred. Safe to call on every edit and on
  // mount — calls coalesce per docId.
  const scheduleThumbnail = useCallback((griddDocId: string) => {
    scheduleRef.current(griddDocId, THUMB_DEBOUNCE_MS, 0);
  }, []);

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((t) => window.clearTimeout(t));
      pending.clear();
    };
  }, []);

  return { thumbnails, scheduleThumbnail };
}

// The scheduler, handed down to each `ContentWindow` so it can request a thumbnail when its
// grid mounts or changes. Scoped to the editor body only (like the snapshot register), so a
// `ContentWindow` in the print/present overlay gets a no-op.
const ThumbnailScheduleContext = createContext<
  ((docId: string) => void) | null
>(null);

export function ThumbnailScheduleProvider({
  schedule,
  children,
}: {
  schedule: (docId: string) => void;
  children: ReactNode;
}) {
  return (
    <ThumbnailScheduleContext.Provider value={schedule}>
      {children}
    </ThumbnailScheduleContext.Provider>
  );
}

// A stable "schedule a thumbnail for this grid" callback. No-op with no griddDocId or outside
// the provider (print/present copies), so it's always safe to call.
export function useScheduleThumbnail() {
  const schedule = useContext(ThumbnailScheduleContext);
  return useCallback(
    (griddDocId: string | null) => {
      if (schedule && griddDocId) schedule(griddDocId);
    },
    [schedule],
  );
}
